import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Menu, ShoppingBag, UserRound } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useLanguage, type Language } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useAuth } from "@/contexts/AuthContext";
import { CURRENCY_LABEL, type Currency } from "@/lib/currency";
import { buildLocalizedPath, stripLangPrefix } from "@/lib/i18n";
import { GUIDES, guidePathFor } from "@/lib/guide-routes";
import { LANGUAGE_SELECTION_EVENT, loadLanguage } from "@/lib/translations";
import { recoverChunkLoadError } from "@/lib/chunkRecovery";
import { LocalizedLink } from "@/components/LocalizedLink";
import { AppDownloadAction } from "@/components/field/AppDownloadAction";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import logo from "@/assets/brand/fluxabee-logo.svg";

const UserMenu = lazy(() =>
  import("@/components/auth/UserMenu").then((m) => ({ default: m.UserMenu })),
);

export const Navbar = () => {
  const { totalItems } = useCart();
  const { t, language } = useLanguage();
  const { currency, setCurrency } = useCurrency();
  const { user, openLoginDialog, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const languageRequest = useRef(0);
  const currentLocation = useRef(location);
  currentLocation.current = location;
  const normalizedPath = stripLangPrefix(location.pathname);
  const changeLanguage = async (next: Language) => {
    window.dispatchEvent(new Event(LANGUAGE_SELECTION_EVENT));
    const version = ++languageRequest.current;
    const previousLocation = location;
    const guide = GUIDES.find((entry) =>
      Object.values(entry.slugs).some((slug) => `/${slug}` === normalizedPath),
    );
    const nextPath = guide
      ? guidePathFor(guide.id, next)
      : buildLocalizedPath(location.pathname, next);
    const destination = nextPath + location.search + location.hash;
    try {
      await loadLanguage(next);
    } catch (error) {
      if (version === languageRequest.current && currentLocation.current === previousLocation) {
        recoverChunkLoadError(error, destination);
      }
      return;
    }
    if (version !== languageRequest.current || currentLocation.current !== previousLocation) return;
    try {
      document.cookie = `fluxabee-lang=${next}; path=/; max-age=31536000; samesite=lax${window.location.protocol === "https:" ? "; secure" : ""}`;
      window.localStorage.setItem("fluxabee-lang", next);
    } catch {
      /* Navigation remains available if persistence is blocked. */
    }
    navigate(destination);
  };
  useEffect(() => () => { languageRequest.current += 1; }, []);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setMenuOpen(false), [location]);
  const links = [
    { to: "/", label: "field.nav.platform" },
    { to: "/platform", label: "field.nav.app" },
    { to: "/products", label: "field.nav.shop" },
    { to: "/guides", label: "field.nav.guides" },
    { to: "/contact", label: "nav.contact" },
  ];
  const isActive = (to: string) =>
    normalizedPath === to ||
    (to === "/products" && normalizedPath.startsWith("/products/")) ||
    (to === "/guides" &&
      GUIDES.some((entry) =>
        Object.values(entry.slugs).some(
          (slug) => normalizedPath === `/${slug}`,
        ),
      ));
  return (
    <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
      <a href="#content-outlet" className="field-skip">
        {t("field.skip")}
      </a>
      <header className="field-navbar" data-scrolled={scrolled}>
        <div className="field-nav-inner field-wrap">
          <LocalizedLink to="/" className="field-brand" aria-label="Fluxabee">
            <img src={logo} alt="" width={184} height={50} />
          </LocalizedLink>
          <nav className="field-nav-links" aria-label={t("platform.nav.main")}>
            {links.map(({ to, label }) => (
              <LocalizedLink
                key={to}
                to={to}
                aria-current={isActive(to) ? "page" : undefined}
              >
                {t(label)}
              </LocalizedLink>
            ))}
          </nav>
          <div className="field-nav-actions">
            <select
              value={language}
              onChange={(e) => changeLanguage(e.target.value as Language)}
              className="field-nav-locale"
              aria-label={t("platform.nav.language")}
            >
              <option value="pl">PL</option>
              <option value="en">EN</option>
              <option value="de">DE</option>
            </select>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as Currency)}
              className="field-nav-locale field-nav-currency"
              aria-label={t("platform.nav.currency")}
            >
              {Object.entries(CURRENCY_LABEL).map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
            <LocalizedLink
              to="/cart"
              className="field-nav-cart"
              aria-label={t("nav.cart")}
            >
              <ShoppingBag size={19} strokeWidth={1.5} aria-hidden="true" />
              {totalItems > 0 && (
                <span className="field-cart-count">{totalItems}</span>
              )}
            </LocalizedLink>
            {user ? (
              <Suspense fallback={null}>
                <UserMenu />
              </Suspense>
            ) : (
              <button
                type="button"
                className="field-nav-account"
                onClick={openLoginDialog}
                aria-label={t("nav.login")}
              >
                <UserRound size={18} strokeWidth={1.5} aria-hidden="true" />
              </button>
            )}
            <AppDownloadAction source="nav" className="field-nav-start" compact />
            <SheetTrigger asChild>
              <button
                type="button"
                className="field-nav-menu"
                aria-label={t("nav.menuLabel")}
              >
                <Menu size={23} strokeWidth={1.5} aria-hidden="true" />
              </button>
            </SheetTrigger>
          </div>
        </div>
      </header>
      <SheetContent
        side="right"
        closeLabel={t("platform.nav.close")}
        aria-describedby={undefined}
        className="field-mobile-menu z-[70] flex w-[min(92vw,400px)] flex-col overflow-y-auto px-6 pb-8 pt-8"
      >
        <SheetTitle className="field-brand mb-7">
          <img src={logo} alt="Fluxabee" width={184} height={50} />
        </SheetTitle>
        <nav aria-label={t("platform.nav.main")}>
          {links.map(({ to, label }) => (
            <LocalizedLink
              key={to}
              to={to}
              aria-current={isActive(to) ? "page" : undefined}
            >
              {t(label)}
              <ArrowUpRight size={21} aria-hidden="true" />
            </LocalizedLink>
          ))}
        </nav>
        <div className="field-mobile-controls">
          <label>
            {t("platform.nav.language")}
            <select
              value={language}
              onChange={(e) => changeLanguage(e.target.value as Language)}
            >
              <option value="pl">Polski</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </select>
          </label>
          <label>
            {t("platform.nav.currency")}
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as Currency)}
            >
              {Object.entries(CURRENCY_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {user ? (
          <>
            <Link to="/account" className="field-button">
              {t("account.myAccount")}
            </Link>
            <button
              className="field-text-link"
              onClick={() => {
                setMenuOpen(false);
                logout();
              }}
            >
              {t("auth.logout")}
            </button>
          </>
        ) : (
          <button
            className="field-button"
            onClick={() => {
              setMenuOpen(false);
              openLoginDialog();
            }}
          >
            {t("nav.login")}
            <UserRound size={18} aria-hidden="true" />
          </button>
        )}
      </SheetContent>
    </Sheet>
  );
};
